// Today screen: pre-shift readiness and live (on-shift) ISFI at every drill
// scope (prototype renderToday).

import React from 'react';
import { cn } from '@/lib/utils';
import {
  REGIONS,
  GROUPS,
  WORKERS,
  isfi,
  notReadyCount,
  onShift,
  DEMO_MODE,
  pct,
  rdyLabel,
  readiness,
  sign,
  wArr,
  workersInGroup,
  workersInRegion,
  wtrBreaches,
} from '../engine/data';
import type { Worker } from '../engine/data';
import { genInsight } from '../engine/insights';
import { cdiTone, lrmLabel, lrmTone, rdyTone } from '../engine/scores';
import { AiInsight } from '../AiPanels';
import { CompBar } from '../ScoreWidgets';
import { useDash } from '../state';
import {
  Banner,
  Chip,
  Dot,
  FBar,
  Grid,
  Kpi,
  KpiRow,
  Panel,
  Pill,
  SecHeader,
  dataTable,
  fedTone,
  toneText,
  toneVar,
} from '../ui';
import type { Tone } from '../ui';
import { ScopeSwitchers } from './past/ScopePickers';

const th = cn(dataTable.th, 'text-center');
const thNum = cn(dataTable.th, 'text-right');
const tdC = cn(dataTable.td, 'text-center');
const tdNum = cn(dataTable.td, dataTable.num);
const clickRow = cn(dataTable.tr, 'cursor-pointer');

const isfiTone = (v: number): Tone => (v >= 75 ? 'bad' : v >= 50 ? 'warn' : 'good');
const avgIsfi = (ws: Worker[]) => (ws.length ? Math.round(ws.reduce((a, w) => a + isfi(w), 0) / ws.length) : 0);

const OfOf: React.FC<{ n: number; total: number }> = ({ n, total }) => (
  <>
    {n}
    <small className='text-base font-normal text-muted-foreground'>/{total}</small>
  </>
);

const MiniBar: React.FC<{ value: number; tone: Tone }> = ({ value, tone }) => (
  <FBar value={value} tone={tone} className='w-[50px]' />
);

// ── Pre-shift ────────────────────────────────────────────────────────────────

const PreshiftTable: React.FC<{ workers: Worker[] }> = ({ workers }) => {
  const { drillWorker } = useDash();
  const notRdy = workers.filter((w) => readiness(w) === 2);
  const monitor = workers.filter((w) => readiness(w) === 1);
  const rows = [...workers].sort((a, b) => readiness(b) - readiness(a) || b.lrm - a.lrm);
  return (
    <>
      {notRdy.length > 0 && (
        <Banner tone='bad'>
          {notRdy.length} worker{notRdy.length > 1 ? 's' : ''} flagged NOT READY —{' '}
          {notRdy.map((w) => w.name).join(', ')}
        </Banner>
      )}
      <KpiRow>
        <Kpi label='Not Ready' value={notRdy.length} tone={notRdy.length ? 'bad' : 'good'} />
        <Kpi label='Monitor' value={monitor.length} tone={monitor.length ? 'warn' : 'good'} />
        <Kpi label='Ready' value={workers.length - notRdy.length - monitor.length} tone='good' />
        <Kpi label='WTR Flags' value={wtrBreaches(workers)} />
      </KpiRow>
      <Panel
        title='Pre-Shift Assessment'
        sub={
          DEMO_MODE.live
            ? 'Biometric scores vs 28-day personal baseline'
            : 'Biometric scores vs 28-day personal baseline · LRM = readiness vs shift demand'
        }
      >
        <div className={dataTable.wrap}>
          <table className={dataTable.table}>
            <thead>
              <tr>
                <th className={dataTable.th}>Worker</th>
                <th className={dataTable.th}>Role</th>
                <th className={th}>Readiness</th>
                <th className={thNum}>FED</th>
                <th className={thNum}>CDI</th>
                {!DEMO_MODE.live && <th className={thNum}>LRM</th>}
                <th className={thNum}>HRV</th>
                <th className={thNum}>vs Base</th>
                <th className={thNum}>RHR</th>
                <th className={thNum}>Sleep</th>
                <th className={th}>WTR</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => {
                const r = readiness(w);
                const dHRV = pct(w.hrv, w.hrv_b);
                const dRHR = pct(w.rhr, w.rhr_b);
                const hrvTone: Tone = dHRV <= -15 ? 'bad' : dHRV <= -8 ? 'warn' : 'good';
                return (
                  <tr key={w.init} className={clickRow} onClick={() => drillWorker(w.init)}>
                    <td className={dataTable.td}>
                      <strong>{w.name}</strong>
                    </td>
                    <td className={cn(dataTable.td, 'text-[11px] text-muted-foreground')}>{w.role}</td>
                    <td className={tdC}>
                      <Pill tone={rdyTone(r)}>{rdyLabel(r)}</Pill>
                      {!DEMO_MODE.live && w.lrm > 20 && (
                        <div className={cn('mt-1 text-[10px]', toneText[lrmTone(w.lrm)])}>{lrmLabel(w.lrm)} LRM</div>
                      )}
                    </td>
                    <td className={cn(tdNum, 'font-semibold', toneText[fedTone(w.fed)])}>{w.fed}</td>
                    <td className={cn(tdNum, 'font-semibold', toneText[cdiTone(w.cdi)])}>{w.cdi}</td>
                    {!DEMO_MODE.live && (
                      <td className={cn(tdNum, 'font-semibold', toneText[lrmTone(w.lrm)])}>
                        {w.lrm > 0 ? `+${w.lrm}` : w.lrm}
                      </td>
                    )}
                    <td className={tdNum}>{w.hrv}</td>
                    <td className={cn(tdNum, toneText[hrvTone])}>
                      {sign(dHRV)}
                      {dHRV}%
                    </td>
                    <td
                      className={cn(
                        tdNum,
                        dRHR >= 8 ? 'text-destructive' : dRHR >= 4 ? 'text-warning' : 'text-muted-foreground',
                      )}
                    >
                      {w.rhr}
                    </td>
                    <td className={tdNum}>{w.slp}h</td>
                    <td className={tdC}>{w.wtrFlag ? <Pill tone='bad'>Breach</Pill> : <Pill tone='good'>OK</Pill>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
};

// ── Live / ISFI ──────────────────────────────────────────────────────────────

const LiveTable: React.FC<{ workers: Worker[] }> = ({ workers }) => {
  const { drillWorker } = useDash();
  const onShiftW = workers.filter(onShift);
  const high = onShiftW.filter((w) => isfi(w) >= 75).length;
  const rows = [...onShiftW].sort((a, b) => isfi(b) - isfi(a));
  return (
    <>
      <KpiRow>
        <Kpi label='On Shift Now' value={onShiftW.length} />
        <Kpi label='High ISFI (≥75)' value={high} tone={high ? 'bad' : 'good'} />
        <Kpi label='Avg ISFI' value={avgIsfi(onShiftW)} />
      </KpiRow>
      {!onShiftW.length ? (
        <Banner tone='info'>No workers currently on shift.</Banner>
      ) : (
        <Panel title='Intra-Shift Fatigue Index (ISFI)' sub='EDA 35% · HR elevation 35% · Self-assessment 30%'>
          <div className={dataTable.wrap}>
            <table className={dataTable.table}>
              <thead>
                <tr>
                  <th className={dataTable.th}>Worker</th>
                  <th className={th}>ISFI</th>
                  <th className={thNum}>EDA</th>
                  <th className={thNum}>HR Elev</th>
                  <th className={thNum}>Assess</th>
                  <th className={dataTable.th}>In-work</th>
                  <th className={dataTable.th}>Out-of-work</th>
                  <th className={dataTable.th}>FED</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((w) => {
                  const fi = isfi(w);
                  const t = isfiTone(fi);
                  const hrEl = w.hr_work_b > 0 ? Math.round(((w.hr_work - w.hr_work_b) / w.hr_work_b) * 100) : 0;
                  const assessPct = Math.round((w.assessScore / 20) * 100);
                  return (
                    <tr key={w.init} className={clickRow} onClick={() => drillWorker(w.init)}>
                      <td className={dataTable.td}>
                        <strong>{w.name}</strong>
                      </td>
                      <td className={tdC}>
                        <span className='inline-flex items-center gap-2'>
                          <FBar value={fi} tone={t} className='w-[100px]' />
                          <span className={cn('text-xs font-semibold', toneText[t])}>{fi}</span>
                        </span>
                      </td>
                      <td className={tdNum}>{w.edaScore}</td>
                      <td
                        className={cn(
                          tdNum,
                          hrEl >= 20 ? 'text-destructive' : hrEl >= 10 ? 'text-warning' : 'text-muted-foreground',
                        )}
                      >
                        {sign(hrEl)}
                        {hrEl}%
                      </td>
                      <td className={tdNum}>{assessPct}%</td>
                      <td className={dataTable.td}>
                        <MiniBar value={w.inWorkFed} tone='info' />
                      </td>
                      <td className={dataTable.td}>
                        <MiniBar value={w.outWorkFed} tone='violet' />
                      </td>
                      <td className={cn(tdNum, 'font-semibold', toneText[fedTone(w.fed)])}>{w.fed}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </>
  );
};

// ── Company-level region cards ───────────────────────────────────────────────

const RegionCardHead: React.FC<{ color: string; name: string; right: string }> = ({ color, name, right }) => (
  <div className='mb-2.5 flex items-center gap-2'>
    <Dot color={color} className='h-2.5 w-2.5' />
    <span className='font-semibold'>{name}</span>
    <span className='ml-auto text-[11px] text-muted-foreground'>{right}</span>
  </div>
);

const CountTile: React.FC<{ n: number; label: string; tone: Tone; alert?: boolean }> = ({ n, label, tone, alert }) => (
  <div
    className={cn(
      'flex-1 rounded-lg border p-2 text-center',
      alert ? 'border-destructive/40 bg-destructive/10' : 'border-transparent bg-muted',
    )}
  >
    <div className={cn('text-[22px] font-bold', toneText[tone])}>{n}</div>
    <div className='text-[10px] text-muted-foreground'>{label}</div>
  </div>
);

const CompanyToday: React.FC<{ tab: string }> = ({ tab }) => {
  const { drillRegion } = useDash();
  const allW = wArr();
  if (tab === 'preshift') {
    const notRdy = allW.filter((w) => readiness(w) === 2);
    const monitor = allW.filter((w) => readiness(w) === 1);
    return (
      <>
        {notRdy.length > 0 && (
          <Banner tone='bad'>
            {notRdy.length} worker{notRdy.length > 1 ? 's' : ''} flagged NOT READY across the company
          </Banner>
        )}
        <KpiRow>
          <Kpi
            label='Not Ready'
            value={<OfOf n={notRdy.length} total={allW.length} />}
            sub='Company-wide'
            tone={notRdy.length ? 'bad' : 'good'}
          />
          <Kpi label='Monitor' value={monitor.length} tone={monitor.length ? 'warn' : 'good'} />
          <Kpi label='Ready' value={allW.length - notRdy.length - monitor.length} tone='good' />
          <Kpi label='WTR Flags' value={wtrBreaches(allW)} />
        </KpiRow>
        <AiInsight>{genInsight('today', allW, { isCompany: true, scopeName: 'the company' })}</AiInsight>
        <SecHeader>Readiness by Region — click to drill in</SecHeader>
        <Grid cols={2}>
          {Object.entries(REGIONS).map(([rid, reg]) => {
            const rw = workersInRegion(rid);
            const rNR = notReadyCount(rw);
            const rMon = rw.filter((w) => readiness(w) === 1).length;
            return (
              <Panel key={rid} className='cursor-pointer transition-colors hover:bg-muted/40'>
                <div onClick={() => drillRegion(rid)}>
                  <RegionCardHead color={reg.color} name={reg.name} right={`${rw.length} workers`} />
                  <div className='flex gap-3'>
                    <CountTile n={rNR} label='Not Ready' tone={rNR > 0 ? 'bad' : 'muted'} alert={rNR > 0} />
                    <CountTile n={rMon} label='Monitor' tone='warn' />
                    <CountTile n={rw.length - rNR - rMon} label='Ready' tone='good' />
                  </div>
                </div>
              </Panel>
            );
          })}
        </Grid>
      </>
    );
  }
  const onShiftAll = allW.filter(onShift);
  const high = onShiftAll.filter((w) => isfi(w) >= 75).length;
  return (
    <>
      <KpiRow>
        <Kpi label='On Shift Now' value={<OfOf n={onShiftAll.length} total={allW.length} />} />
        <Kpi label='High ISFI (≥75)' value={high} tone={high ? 'bad' : 'good'} />
        <Kpi label='Company Avg ISFI' value={avgIsfi(onShiftAll)} />
      </KpiRow>
      <AiInsight>{genInsight('today', allW, { isCompany: true, scopeName: 'the company' })}</AiInsight>
      <SecHeader>Live Status by Region</SecHeader>
      <Grid cols={2}>
        {Object.entries(REGIONS).map(([rid, reg]) => {
          const rw = workersInRegion(rid).filter(onShift);
          const hi = rw.filter((w) => isfi(w) >= 75).length;
          const avg = avgIsfi(rw);
          return (
            <Panel key={rid} className='cursor-pointer transition-colors hover:bg-muted/40'>
              <div onClick={() => drillRegion(rid)}>
                <RegionCardHead color={reg.color} name={reg.name} right={`${rw.length} on shift`} />
                <CompBar val={avg} max={100} color={toneVar[isfiTone(avg)]} />
                <div className='mt-1 text-[10px] text-muted-foreground'>
                  Avg ISFI · {hi > 0 ? <span className='text-destructive'>{hi} high</span> : 'None high'}
                </div>
              </div>
            </Panel>
          );
        })}
      </Grid>
    </>
  );
};

// ── Screen ───────────────────────────────────────────────────────────────────

export const TodayScreen: React.FC = () => {
  const dash = useDash();
  const tab = dash.todayTab === 'live' ? 'live' : 'preshift';

  const tabFilter = (
    <div className='flex flex-wrap items-center gap-2'>
      <Chip active={tab === 'preshift'} onClick={() => dash.patch({ todayTab: 'preshift' })}>
        Pre-Shift Readiness
      </Chip>
      <Chip active={tab === 'live'} onClick={() => dash.patch({ todayTab: 'live' })}>
        Live — On Shift
      </Chip>
    </div>
  );

  // Scoped (non-company) screens share: pickers, tabs, insight, then the table.
  let scoped: Worker[] | null = null;
  let insightOpts = { scopeName: '' } as { scopeName: string; regionId?: string };
  if (dash.drillScope === 'region' && dash.activeRegion) {
    scoped = workersInRegion(dash.activeRegion);
    insightOpts = { regionId: dash.activeRegion, scopeName: REGIONS[dash.activeRegion].name };
  } else if (dash.drillScope === 'group' && dash.activeGroup !== null) {
    scoped = workersInGroup(dash.activeGroup);
    insightOpts = { scopeName: GROUPS[dash.activeGroup].name };
  } else if (dash.drillScope === 'individual' && dash.activeWorker) {
    scoped = [WORKERS[dash.activeWorker]];
    insightOpts = { scopeName: WORKERS[dash.activeWorker].name };
  }

  return (
    <div className='flex flex-col gap-4'>
      {scoped ? (
        <>
          <ScopeSwitchers />
          {tabFilter}
          <AiInsight>{genInsight('today', scoped, insightOpts)}</AiInsight>
          {tab === 'preshift' ? <PreshiftTable workers={scoped} /> : <LiveTable workers={scoped} />}
        </>
      ) : (
        <>
          {tabFilter}
          <CompanyToday tab={tab} />
        </>
      )}
    </div>
  );
};
